import { IconButton, Pagination, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Typography, Button, Radio, FormControlLabel, RadioGroup, Box, Paper } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import Swal from "sweetalert2";
import { getPermissionData, getUserData } from "../../Actions/common";
import { deleteMultiplePlansAction, listAllPlansAction, updatePlanStatusAction } from "../../Actions/PlanManagementActions";
import { MyWarningAmberIcon } from "../../Assets/svg/MyIcons";
import BreadcrumbsComponent from "../../components/Common/Breadcrumbs";
import { ITEMS_PER_PAGE_OPTIONS } from "../../config";
import CustomSnackbar from "../Common/snackBar";
import AddEditPlan from "./AddEditPlan";
import renderStatusBadge from "../../utils/badges";
import showConfirmation from "../../utils/Conformation";
import { deleteMultipleKycPlansAction, listAllKycPlansAction, updateKycPlanStatusAction } from "../../Actions/KycPlanManagemantAction";
import { deleteTripolaPlansAction, listAllTripolaPlansAction, updateTripolaPlanStatusAction } from "../../Actions/TripolaPlanActions";
import AddEditKycPlan from "../KycPlanManagement/AddEditKycPlan";
import AddEditTripolaPlan from "../TripolaPlan/AddEditPlan"
import DataTable from "../../components/DataTable/DataTable";

const portalApiMap = {
  qbot: {
    updateStatus: updatePlanStatusAction,
    deletePlan: deleteMultiplePlansAction,
  },
  kyc: {
    updateStatus: updateKycPlanStatusAction,
    deletePlan: deleteMultipleKycPlansAction,
  },
  tripola: {
    updateStatus: updateTripolaPlanStatusAction,
    deletePlan: deleteTripolaPlansAction,
  },
};

const DescriptionModal = ({ open, handleClose, description }) => {
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle className="card-header text-bg-primary fw-bold fs-2">Plan Description</DialogTitle>
      <DialogContent dividers>
        <Typography
          variant="body1"
          sx={{ fontSize: "16px", color: "#333", whiteSpace: "pre-line" }}
        >
          {description || "No description available."}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          variant="contained"
          color="primary"
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};



const PlanManagementListing = () => {
  const [plansListData, setPlansListData] = useState([]);

  const [openModal, setOpenModal] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [shouldFetchData, setShouldFetchData] = useState(1);
  const [selectedRowsPerPage, setSelectedRowsPerPage] = useState(ITEMS_PER_PAGE_OPTIONS[0]);
  const [currentId, setCurrentId] = useState(null);
  const [isDescriptionOpen, setIsDescriptionOpen] = useState({
    isOpen: false,
    description: "",
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const userData = localStorage.getItem("userData") ? getUserData() : null;
  const permissionData = localStorage.getItem(`${userData?.id}_permissionData`) ? getPermissionData(`${userData?.id}_permissionData`) : [];
  const hasPermission = permissionData[0]?.role_permissions?.find((m) => m?.module && m?.module?.slug === "plan-management");
  const [activeUsersWarning, setActiveUsersWarning] = useState({
    isOpen: false,
    plan: null,
    actionType: null, // 'delete' or 'deactivate'
  });

  const [filterValues, setFilterValues] = useState({
    search: "",
    status: "all",
    portal: "qbot",
  });
  const [openPortalSelector, setOpenPortalSelector] = useState(false);
  const [selectedPortalForCreate, setSelectedPortalForCreate] = useState("qbot");

  const getPlansList = useCallback(async () => {

    setLoading(true);

    let apiCall;

    if (filterValues.portal === "qbot") {
      apiCall = listAllPlansAction({
        page: currentPage,
        pageSize: selectedRowsPerPage,
        searchValue: filterValues.search,
        status: filterValues.status !== "all" ? filterValues.status === "1" : null,
      });
    }

    if (filterValues.portal === "kyc") {
      apiCall = listAllKycPlansAction(
        currentPage,
        selectedRowsPerPage,
        filterValues.search,
        filterValues.status !== "all" ? filterValues.status === "1" : null
      );
    }

    if (filterValues.portal === "tripola") {
      apiCall = listAllTripolaPlansAction(
        currentPage,
        selectedRowsPerPage,
        filterValues.search,
        filterValues.status !== "all" ? filterValues.status === "1" : null,
        false
      );
    }

    try {
      const res = await apiCall;
      const totalRecords = res?.data?.totalRecords || 0;

      const data = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.data)
          ? res.data.data
          : [];

      let formattedData = [];

      if (filterValues.portal === "qbot") {
        formattedData = (data || []).map((item) => ({
          ...item,
          portalType: "qbot",
        }));
      }

      if (filterValues.portal === "kyc") {
        formattedData = (data || []).map((item) => ({
          ...item,
          portalType: "kyc",
          monthly_price: item.monthly_base_price,
          yearly_price: item.yearly_base_price,
          isActive: item.isActive,
        }));
      }

      if (filterValues.portal === "tripola") {
        formattedData = (data || []).map((item) => ({
          ...item,
          portalType: "tripola",
          name: item.plan_name,
          description: item.plan_description,
          monthly_price: item.monthly_price,
          yearly_price: item.yearly_price,
          isActive: item.is_active,
          version: item.version || 1,
          activeUsersCount: item.activeUsersCount || 0,
        }));
      }

      setPlansListData(formattedData);
      setTotalRecords(totalRecords || 0);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.message || "Something went wrong",
        severity: "error",
      });
    }

    setLoading(false);
  }, [
    currentPage,
    selectedRowsPerPage,
    filterValues
  ]);

  useEffect(() => {
    if (shouldFetchData) {
      getPlansList();
      setShouldFetchData(0);
    }
  }, [shouldFetchData]);

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };


  const handleTogglePlanStatus = async (e, plan) => {
    e.stopPropagation();

    // Check if plan has active users and is currently active
    if (plan.activeUsersCount > 0 && plan.isActive) {
      // Show warning dialog instead of deactivating
      setActiveUsersWarning({
        isOpen: true,
        plan: plan,
        actionType: "deactivate",
      });
      return;
    }

    const newStatus = !plan.isActive;
    const actionText = newStatus ? "Activate" : "Deactivate";

    const result = await Swal.fire({
      title: `${actionText} Plan?`,
      text: `Are you sure you want to ${actionText.toLowerCase()} the plan "${plan.name}" (v${plan.version})?`,
      icon: newStatus ? "question" : "warning",
      showCancelButton: true,
      confirmButtonColor: newStatus ? "#28a745" : "#dc3545",
      cancelButtonColor: "#777777",
      confirmButtonText: `Yes, ${actionText}`,
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        const selectedPortal = plan.portalType || filterValues.portal;

        const res = await portalApiMap[selectedPortal].updateStatus(plan.id, {
          isActive: newStatus,
        });

        setSnackbar({
          open: true,
          message: res.message || `Plan ${actionText.toLowerCase()}d successfully`,
          severity: "success",
        });

        getPlansList();
      } catch (error) {
        setSnackbar({
          open: true,
          message: error?.message || "Failed to update plan status",
          severity: "error",
        });
      }
    }
  };

  //  Active Users Warning Dialog
  // const ActiveUsersWarningDialog = ({ open, handleClose, plan, actionType }) => {
  //     const actionText = actionType === "delete" ? "deleted" : "deactivated";
  //     const actionTitle = actionType === "delete" ? "Delete" : "Deactivate";

  //     return (
  //         <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
  //             <DialogTitle className="card-header text-bg-warning fw-bold fs-2 d-flex align-items-center">
  //                 <MyWarningAmberIcon className="me-2" />
  //                 Cannot {actionTitle} Plan
  //             </DialogTitle>
  //             <DialogContent dividers>
  //                 <Typography variant="body1" sx={{ fontSize: "16px", color: "#333", mb: 2 }}>
  //                     <strong>{plan?.name}</strong> (v{plan?.version}) cannot be {actionText} because it currently has{" "}
  //                     <strong style={{ color: "#28a745" }}>{plan?.activeUsersCount}</strong> active {plan?.activeUsersCount === 1 ? "user" : "users"}.
  //                 </Typography>
  //                 <Typography variant="body2" sx={{ fontSize: "14px", color: "#555" }}>
  //                     Please ensure all users are migrated to a different plan before attempting to {actionText.slice(0, -1)} this plan.
  //                 </Typography>
  //             </DialogContent>
  //             <DialogActions>
  //                 <Button onClick={handleClose} variant="contained" color="primary">
  //                     Understood
  //                 </Button>
  //             </DialogActions>
  //         </Dialog>
  //     );
  // };

  //  Active Users Warning Dialog
  const ActiveUsersWarningDialog = ({ open, handleClose, plan, actionType }) => {
    const actionText = actionType === "delete" ? "deleted" : "deactivated";
    const actionTitle = actionType === "delete" ? "Delete" : "Deactivate";

    return (
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle className="card-header text-bg-warning fw-bold fs-2 d-flex align-items-center">
          <MyWarningAmberIcon className="me-2" />
          Cannot {actionTitle} Plan
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" sx={{ fontSize: "16px", color: "#333", mb: 2 }}>
            <strong>{plan?.name}</strong> (v{plan?.version}) cannot be {actionText} because it currently has{" "}
            <strong style={{ color: "#28a745" }}>{plan?.activeUsersCount}</strong> active {plan?.activeUsersCount === 1 ? "user" : "users"}.
          </Typography>
          <Typography variant="body2" sx={{ fontSize: "14px", color: "#555" }}>
            Please ensure all users are migrated to a different plan before attempting to {actionText.slice(0, -1)} this plan.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="contained" color="primary">
            Understood
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const handleCreatePlan = async (e, id, portalType) => {
    const selectedPortal = portalType || filterValues.portal;

    if (id) {
      setCurrentId({
        id,
        portalType: selectedPortal,
      });
    } else {
      setCurrentId({
        id: null,
        portalType: selectedPortal,
      });
      setSelectedPortalForCreate("qbot");
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    if (openModal) setOpenModal(false);
    if (isDescriptionOpen.isOpen)
      setIsDescriptionOpen({
        isOpen: false,
        description: "",
      });
    if (activeUsersWarning.isOpen)
      setActiveUsersWarning({
        isOpen: false,
        plan: null,
        actionType: null,
      });
    setCurrentId(null);
  };

  const handleDeletePlan = async (e, deleteId, portalType) => {
    e.stopPropagation();

    const selectedPortal = portalType || filterValues.portal;

    // Find the plan to check active users
    const planToDelete = plansListData.find((p) => p.id === deleteId);

    // Check if plan has active users - Show warning dialog instead of deleting
    if (planToDelete && planToDelete.activeUsersCount > 0) {
      setActiveUsersWarning({
        isOpen: true,
        plan: planToDelete,
        actionType: "delete",
      });
      return;
    }

    // Proceed with normal deletion flow
    await showConfirmation("simpleDelete", {
      entityName: "Plan",
      entityId: deleteId,
      customText: "You will not be able to recover this plan!",
      action: async (id) => {
        return await portalApiMap[selectedPortal].deletePlan(id);
      },
      onSuccess: () => {
        getPlansList();
      },
      onError: (error) => {
        setSnackbar({
          open: true,
          message: error.response?.data?.message,
          severity: "error",
        });
      },
      showCancelledMsg: true,
    });
  };



  // const handleRowSelection = (event, ele) => {
  //   const selectedId = ele.id;

  //   if (event.target.checked) {
  //     // Add the selected ID to the array
  //     setSelectedRows((prevSelectedRows) => [...prevSelectedRows, selectedId]);
  //   } else {
  //     // Remove the selected ID from the array
  //     setSelectedRows((prevSelectedRows) => prevSelectedRows.filter((id) => id !== selectedId));
  //   }
  // };

  // const handleSelectAll = (event) => {
  //   if (event.target.checked) {
  //     // Select all records
  //     const allIds = plansListData.map((ele) => ele.id);
  //     setSelectedRows(allIds);
  //   } else {
  //     // Deselect all records
  //     setSelectedRows([]);
  //   }
  // };

  const actions = {
    edit: hasPermission?.canEdit,
    delete: hasPermission?.canDelete,
    activate: hasPermission?.canPublish,
    deactivate: hasPermission?.canPublish,
  };

  const handleAction = (type, row) => {
    switch (type) {
      case "edit":
        handleCreatePlan(
          null,
          row.id,
          row.portalType
        );
        break;

      case "delete":
        handleDeletePlan(
          {
            stopPropagation: () => { },
          },
          row.id,
          row.portalType
        );
        break;

      case "activate":
      case "deactivate":
        handleTogglePlanStatus(
          {
            stopPropagation: () => { },
          },
          row
        );
        break;

      default:
        break;
    }
  };



  useEffect(() => { }, []);

  const activePortal = plansListData?.[0]?.portalType || "qbot";

  const commonColumns = [
    {
      field: "name",
      headerName: "Plan Name",
      flex: 1,
      minWidth: 180,
      renderCell: (params) => (
        <span style={{ fontSize: "16px", fontWeight: 500, color: "#333" }}>
          {params.row.name || params.row.plan_name || "N/A"}
        </span>
      ),
    },
    {
      field: "description",
      headerName: "Description",
      flex: 2,
      minWidth: 250,
      renderCell: (params) => {
        const text =
          params.row.description || params.row.plan_description || "N/A";

        const truncatedText =
          text.length > 40 ? text.substring(0, 40) + "..." : text;

        return (
          <span
            style={{
              fontSize: "16px",
              fontWeight: 400,
              color: "#555",
              cursor: "pointer",
              textDecoration: "underline",
            }}
            onClick={() =>
              setIsDescriptionOpen({
                isOpen: true,
                description: text,
              })
            }
          >
            {truncatedText}
          </span>
        );
      },
    },
  ];
  const qbotColumns = [
    {
      field: "user_limit_type",
      hide: activePortal !== "qbot",
      headerName: "User Limit",
      flex: 1,
      minWidth: 150,
      sortable: false,
      align: "center", // centers the cell content
      headerAlign: "center", // centers the header text
      renderCell: (params) => {
        const statusMap = {
          // 0:{ text: "limited", status: "warning" }
          1: { text: "Unlimited", status: "success" },
        };
        const type = params.row?.user_limit_type;
        const value = params.row?.user_limit_value;

        if (statusMap[type]) {
          const { text, status } = statusMap[type];
          return renderStatusBadge({ status, title: text });
        }
        return value || "0";
      },
    },
    {
      field: "message_limit_type",
      hide: activePortal !== "qbot",
      headerName: "Message Limit",
      flex: 1,
      minWidth: 150,
      sortable: false,
      align: "center", // centers the cell content
      headerAlign: "center", // centers the header text
      renderCell: (params) => {
        const statusMap = {
          // 0:{ text: "limited", status: "warning" }
          1: { text: "Unlimited", status: "success" },
        };
        const type = params.row?.message_limit_type;
        const value = params.row?.message_limit_value;

        if (statusMap[type]) {
          const { text, status } = statusMap[type];
          return renderStatusBadge({ status, title: text });
        }
        return value || "0";
      },
    },
    {
      field: "agent_training_limit_type",
      hide: activePortal !== "qbot",
      headerName: "Agent Training Limit",
      flex: 1,
      minWidth: 150,
      sortable: false,
      align: "center", // centers the cell content
      headerAlign: "center", // centers the header text
      renderCell: (params) => {
        const statusMap = {
          // 0:{ text: "limited", status: "warning" }
          1: { text: "Unlimited", status: "success" },
        };
        const type = params.row?.agent_training_limit_type;
        const value = params.row?.agent_training_limit_value;

        if (statusMap[type]) {
          const { text, status } = statusMap[type];
          return renderStatusBadge({ status, title: text });
        }
        return value || "0";
      },
    },
    {
      field: "activeUsersCount",
      hide: activePortal !== "qbot",
      headerName: "Active Users",
      flex: 1,
      minWidth: 120,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Chip
          label={params.row.activeUsersCount || 0}
          size="small"
          style={{
            fontWeight: 500,
            color: "white",
            backgroundColor: params.row.activeUsersCount > 0 ? "#28a745" : "#6c757d",
          }}
        />
      ),
    },
    {
      field: "version",
      headerName: "Version",
      flex: 1,
      minWidth: 100,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Chip
          label={params.row.version || 1}
          size="small"
          style={{
            fontWeight: 500,
            color: "whitesmoke",
            backgroundColor: "#22419e",
          }}
        />
      ),
    },
  ];
  const kycColumns = [
    {
      field: "monthly_base_price",
      headerName: "Monthly Base Price",
      flex: 1,
      minWidth: 120,
      align: "center",
    },
    {
      field: "monthly_included_verification",
      headerName: "Monthly Included Verification",
      flex: 1,
      minWidth: 120,
      align: "center",
    },
    {
      field: "yearly_base_price",
      headerName: "Yearly Base Price",
      flex: 1,
      minWidth: 120,
      align: "center",
    },
    {
      field: "yearly_included_verification",
      headerName: "Yearly Included Verification",
      flex: 1,
      minWidth: 120,
      align: "center",
    },
    {
      field: "version",
      headerName: "Version",
      flex: 1,
      minWidth: 100,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Chip
          label={params.row.version || 1}
          size="small"
          style={{
            fontWeight: 500,
            color: "whitesmoke",
            backgroundColor: "#22419e",
          }}
        />
      ),
    },
  ];
  const tripolaColumns = [
    {
      field: "monthly_price",
      headerName: "Monthly Price",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <span>
          {params.row.monthly_price !== undefined
            ? `${params.row.monthly_price}`
            : "N/A"}
        </span>
      ),
    },
    {
      field: "yearly_price",
      headerName: "Yearly Price",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <span>
          {params.row.yearly_price !== undefined
            ? `${params.row.yearly_price}`
            : "N/A"}
        </span>
      ),
    },
  ];
  const endColumns = [
    {
      field: "isActive",
      headerName: "Is Active",
      flex: 1,
      minWidth: 130,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const statusMap = {
          true: { text: "Active", status: "success" },
          false: { text: "Inactive", status: "default" },
          1: { text: "Active", status: "success" },
          0: { text: "Inactive", status: "default" },
        };

        const statusCode = params.row?.isActive ?? 0;
        const { text, status } = statusMap[statusCode] || {
          text: "Unknown",
          status: "default",
        };

        return renderStatusBadge({ status, title: text });
      },
    },
  ];
  const columns = [
    ...commonColumns,
    ...(activePortal === "qbot" ? qbotColumns : []),
    ...(activePortal === "kyc" ? kycColumns : []),
    ...(activePortal === "tripola" ? tripolaColumns : []),
    ...endColumns,
  ];
  const isCreateMode = !currentId?.id;

  const portalToRender = isCreateMode
    ? selectedPortalForCreate
    : currentId?.portalType;

  const handleToast = (severity, message) => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const changePortalFilter = (portal) => {
    setFilterValues((prev) => ({
      ...prev,
      portal,
    }));

    setCurrentPage(1);
    setShouldFetchData(1);
  };

  const filters = [
    {
      name: "search",
      label: "Search",
      type: "search",
      placeholder: "Search Plan",
    },

    {
      name: "status",
      label: "Status",
      type: "select",
      defaultValue: "all",
      options: [
        {
          label: "All Status",
          value: "all",
        },
        {
          label: "Active",
          value: "1",
        },
        {
          label: "Inactive",
          value: "0",
        },
      ],
    },

    {
      name: "portal",
      label: "Portal",
      type: "select",
      defaultValue: "qbot",
      options: [
        {
          label: "Qbot",
          value: "qbot",
        },
        {
          label: "KYC",
          value: "kyc",
        },
        {
          label: "Tripola",
          value: "tripola",
        },
      ],
    },
  ];

  const handleFilterApply = (values) => {
    setFilterValues(values);

    setCurrentPage(1);
    setShouldFetchData(1);
  };

  const handleFilterReset = () => {
    setFilterValues({
      search: "",
      status: "all",
      portal: "qbot",
    });

    setCurrentPage(1);
    setShouldFetchData(1);
  };

  return (
    <div id="kt_content_container">
      <BreadcrumbsComponent />

      <DataTable
        columns={columns}
        rows={plansListData}
        loading={loading}
        totalCount={totalRecords}
        page={currentPage}
        pageSize={selectedRowsPerPage}
        filters={filters}
        filterValues={filterValues}
        actions={actions}
        onFilterApply={handleFilterApply}
        onFilterReset={handleFilterReset}
        onPageChange={(page) => {
          setCurrentPage(page);
          setShouldFetchData(1);
        }}
        onPageSizeChange={(size) => {
          setSelectedRowsPerPage(size);
          setCurrentPage(1);
          setShouldFetchData(1);
        }}
        onAction={handleAction}
        hasCreatePermission={hasPermission?.canCreate}
        addButtonText="ADD"
        // onAddClick={(e) =>
        //   handleCreatePlan(
        //     e,
        //     null,
        //     filterValues.portal
        //   )
        // }
        onAddClick={() => {
          setSelectedPortalForCreate("qbot");
          setOpenPortalSelector(true);
        }}
        getRowId={(row) => row.id}
      />

      {openModal && (
        <>
          {portalToRender === "qbot" && (
            <AddEditPlan
              key={`qbot-${currentId?.id || "new"}`}
              open={openModal}
              handleClose={handleCloseModal}
              id={currentId?.id}
              refetchPlanList={getPlansList}
              handleToast={handleToast}
              selectedPortalForCreate={selectedPortalForCreate}
              setSelectedPortalForCreate={setSelectedPortalForCreate}
              changePortalFilter={changePortalFilter}
            />
          )}

          {portalToRender === "kyc" && (
            <AddEditKycPlan
              key={`kyc-${currentId?.id || "new"}`}
              open={openModal}
              handleClose={handleCloseModal}
              id={currentId?.id}
              refetchPlanList={getPlansList}
              handleToast={handleToast}
              selectedPortalForCreate={selectedPortalForCreate}
              setSelectedPortalForCreate={setSelectedPortalForCreate}
              changePortalFilter={changePortalFilter}
            />
          )}

          {portalToRender === "tripola" && (
            <AddEditTripolaPlan
              key={`tripola-${currentId?.id || "new"}`}
              open={openModal}
              handleClose={handleCloseModal}
              id={currentId?.id}
              refetchPlanList={getPlansList}
              handleToast={handleToast}
              selectedPortalForCreate={selectedPortalForCreate}
              setSelectedPortalForCreate={setSelectedPortalForCreate}
              changePortalFilter={changePortalFilter}
            />
          )}
        </>
      )}
      {isDescriptionOpen && (
        <DescriptionModal
          open={isDescriptionOpen.isOpen}
          handleClose={handleCloseModal}
          description={isDescriptionOpen.description}
        />
      )}
      {activeUsersWarning.isOpen && (
        <ActiveUsersWarningDialog
          open={activeUsersWarning.isOpen}
          handleClose={handleCloseModal}
          plan={activeUsersWarning.plan}
          actionType={activeUsersWarning.actionType}
        />
      )}
      <Dialog
        open={openPortalSelector}
        onClose={() => setOpenPortalSelector(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle
          className="text-bg-primary"
          sx={{
            fontWeight: 600,
            textAlign: "center",
            pb: 1,
            mb: 3,
          }}
        >
          Select Portal
          <Typography
            variant="body2"
            align="center"
            sx={{ mb: 1 }}
          >
            Choose the portal for which you want to create a plan.
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, mb: 1 }}>
          <RadioGroup
            value={selectedPortalForCreate}
            onChange={(e) =>
              setSelectedPortalForCreate(e.target.value)
            }
          >
            {[
              {
                value: "qbot",
                title: "Qbot",
                description: "Create chatbot subscription plans",
              },
              {
                value: "kyc",
                title: "KYC",
                description: "Create verification service plans",
              },
              {
                value: "tripola",
                title: "Tripola",
                description: "Create travel platform plans",
              },
            ].map((portal) => (
              <Paper
                key={portal.value}
                elevation={0}
                sx={{
                  mb: 2,
                  border: "1px solid",
                  borderColor:
                    selectedPortalForCreate === portal.value
                      ? "#22419e"
                      : "grey.300",
                  borderRadius: 2,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  bgcolor:
                    selectedPortalForCreate === portal.value
                      ? "primary.50"
                      : "white",
                  "&:hover": {
                    borderColor: "#22419e",
                  },
                }}
                onClick={() =>
                  setSelectedPortalForCreate(portal.value)
                }
              >
                <FormControlLabel
                  value={portal.value}
                  control={<Radio sx={{
                    color: "#22419e",
                    "&.Mui-checked": {
                      color: "#22419e",
                    },
                  }} />}
                  sx={{
                    width: "100%",
                    m: 0,
                    px: 2,
                    py: 1,
                    alignItems: "flex-start",
                  }}
                  label={
                    <Box>
                      <Typography fontWeight={600}>
                        {portal.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                      >
                        {portal.description}
                      </Typography>
                    </Box>
                  }
                />
              </Paper>
            ))}
          </RadioGroup>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            justifyContent: "space-between",
          }}
        >
          <Button
            variant="outlined"
            onClick={() => setOpenPortalSelector(false)}
            sx={{
              borderColor: "#22419e",
              color: "#22419e",
              "&:hover": {
                borderColor: "#22419e",
                backgroundColor: "rgba(35, 65, 158, 0.08)",
              },
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={() => {
              setCurrentId({
                id: null,
                portalType: selectedPortalForCreate,
              });

              setOpenPortalSelector(false);
              setOpenModal(true);
            }}
            className="text-bg-primary"
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleCloseSnackbar}
      />
    </div>
  );
};

export default PlanManagementListing;
